import type { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';
import type { Image } from '@sanity/types';
import { computed, reactive, ref, watch } from 'vue';
import { IMAGE_FIT } from '@pkg/sanity-utils/constants/imageFit';
import { getImageDimensions } from './getImageDimensions';
import type { SanityImageLayoutOptions, SanityImageOptions } from './types';

const LARGEST_VIEWPORT = 1920;

const DEFAULT_MIN_STEP = 0.15;
const DEFAULT_WIDTH_STEPS = [400, 600, 850, 1000, 1150];
const DEFAULT_FULL_WIDTH_STEPS = [360, 414, 768, 1366, 1536, 1920];

/**
 * See: https://hdoro.dev/performant-sanity-io-images
 */
export function useImageProps(
  image: Image & { alt?: string; fit?: IMAGE_FIT } | undefined | null,
  options: SanityImageLayoutOptions = {},
  imageOptions: SanityImageOptions = {},
) {
  const { $urlFor } = useNuxtApp() as ReturnType<typeof useNuxtApp> & {
    $urlFor: (source: Image) => ImageUrlBuilder;
  };

  const opts = reactive({
    image,
    maxWidth: options.maxWidth || LARGEST_VIEWPORT,
    minimumWidthStep: options.minimumWidthStep || DEFAULT_MIN_STEP,
    customWidthSteps: options.customWidthSteps,
    sizes: options.sizes,
  });

  const src = ref<string>();
  const srcset = ref<string>();
  const sizes = ref<string>();
  const width = ref<number>();
  const height = ref<number>();

  const imageProperties = computed(() => {
    if (!opts.image?.asset?._ref) {
      return {};
    }

    let builder = $urlFor(opts.image).fit('max').auto('format');

    const imageDimensions = getImageDimensions(opts.image);

    if (opts.image?.fit === IMAGE_FIT.CONTAIN) {
      builder = builder.ignoreImageParams();

      if (opts.image.crop && imageDimensions) {
        const rect = {
          left: imageDimensions.width * opts.image.crop.left,
          top: imageDimensions.height * opts.image.crop.top,
          width: imageDimensions.width * ((1 - opts.image.crop.right) - opts.image.crop.left),
          height: imageDimensions.height * ((1 - opts.image.crop.bottom) - opts.image.crop.top),
        };

        builder = builder.rect(
          Math.round(rect.left),
          Math.round(rect.top),
          Math.round(rect.width),
          Math.round(rect.height),
        );
      }
    }

    if (!imageDimensions) {
      return {};
    }

    const baseSizes = [
      opts.maxWidth,
      ...(opts.customWidthSteps || (opts.maxWidth === LARGEST_VIEWPORT ? DEFAULT_WIDTH_STEPS : DEFAULT_FULL_WIDTH_STEPS)
      ),
    ];

    const candidateSizes = Array.from(
      new Set([
        ...baseSizes,
        ...baseSizes.map(size => size * 2),
        ...baseSizes.map(size => size * 3),
      ]),
    )
      .sort((a, b) => a - b)
      .filter(
        size =>
          size <= imageDimensions.width * 1.1
          && size <= opts.maxWidth * 3,
      )
      .filter((size, i, arr) => {
        const nextSize = arr[i + 1];
        if (nextSize) {
          return nextSize / size > opts.minimumWidthStep + 1;
        }

        return true;
      });

    const retinaSizes = candidateSizes.length ? candidateSizes : [imageDimensions.width];

    return {
      src: builder
        .withOptions({ ...imageOptions, width: imageOptions.width ?? imageOptions.maxWidth, height: imageOptions.height })
        .url(),

      srcset: retinaSizes
        .map(size => `${builder
          .withOptions({
            ...imageOptions,
            width: size,
            height: imageOptions.height ? Math.round((size / (imageOptions.width ?? size)) * imageOptions.height) : undefined,
          }).url()} ${size}w`)
        .join(', '),
      sizes:
        opts.maxWidth === LARGEST_VIEWPORT
          ? '100vw'
          : opts.sizes || `(max-width: ${opts.maxWidth}px) 100vw, ${opts.maxWidth}px`,

      width: retinaSizes[retinaSizes.length - 1],
      height: Math.round(retinaSizes[retinaSizes.length - 1] / imageDimensions.aspectRatio),
    };
  });

  watch(imageProperties, (newImageProperties) => {
    src.value = newImageProperties.src;
    srcset.value = newImageProperties.srcset;
    sizes.value = newImageProperties.sizes;
    width.value = newImageProperties.width;
    height.value = newImageProperties.height;
  }, {
    immediate: true,
  });

  return {
    src,
    srcset,
    sizes,
    width,
    height,
  };
}
